import { lazy, Suspense, useCallback, useEffect, useState } from "react";

import { Spinner } from "@blueprintjs/core";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useHotkeys } from "react-hotkeys-hook";

import { Button } from "@/components/ui/button";

export const SLIDE_FOOTER_HEIGHT = 60;

export function PDFViewerWithControls(props: {
  fileUrl: string;
  onPageChange: (
    /** 0-based */
    pageIndex: number,
  ) => void;
  containerAspectRatio?: number;
}) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setPageNumber(1);
    setNumPages(numPages);
  }

  useEffect(() => {
    setNumPages(null);
  }, [props.fileUrl]);

  useEffect(() => {
    props.onPageChange(pageNumber - 1);
  }, [pageNumber]);

  const handlePrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  }, []);
  const handleNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || 1));
  }, [numPages]);

  useHotkeys("left", handlePrevPage, [handlePrevPage]);
  useHotkeys("right", handleNextPage, [handleNextPage]);

  const navigationControls = (
    <div
      className="grid grid-flow-col justify-center items-center text-white gap-4"
      style={{ height: SLIDE_FOOTER_HEIGHT }}
    >
      <Button
        size="md"
        variant="glass"
        intent="secondary"
        disabled={pageNumber <= 1}
        onClick={handlePrevPage}
      >
        <ChevronLeft color="white" size={16} />
      </Button>
      <div className="text-[16px] grid grid-flow-col gap-3xs">
        <div className="w-10">{pageNumber}</div>
        <div className="w-5">of</div>
        <div className="w-10">{numPages || "-"}</div>
      </div>
      <Button
        size="md"
        variant="glass"
        intent="secondary"
        disabled={numPages ? pageNumber >= numPages : true}
        onClick={handleNextPage}
      >
        <ChevronRight color="white" size={16} />
      </Button>
    </div>
  );

  return (
    <div className="grid grid-rows-[1fr_auto] bg-black rounded-sm pb-4">
      <div className="overflow-hidden">
        <PDFViewer
          url={props.fileUrl}
          pageNumber={pageNumber}
          onLoadSuccess={onDocumentLoadSuccess}
          containerAspectRatio={props.containerAspectRatio}
        />
      </div>
      {navigationControls}
    </div>
  );
}

const PDFViewerClient = lazy(() =>
  import("./pdf-viewer-client").then((module) => ({
    default: module.PDFViewerClient,
  })),
);

interface PDFViewerProps {
  url: string;
  /** 1-based index. First page is 1, not 0. */
  pageNumber: number;
  onLoadSuccess?: (_: { numPages: number }) => void;
  containerAspectRatio?: number;
}

export function PDFViewer(props: PDFViewerProps) {
  return (
    <div className="grid w-full h-full items-center">
      <Suspense
        fallback={
          <div className="w-full overflow-hidden grid">
            <Spinner size={24} />
          </div>
        }
      >
        <PDFViewerClient
          url={props.url}
          pageNumber={props.pageNumber}
          onLoadSuccess={props.onLoadSuccess}
          containerAspectRatio={props.containerAspectRatio}
        />
      </Suspense>
    </div>
  );
}
